import api from './client'
import { unwrap, type ApiEnvelope } from './global'

export type TaskCollection = 'active' | 'waiting' | 'stopped'

export type TaskState =
  | 'active'
  | 'pending'
  | 'waiting'
  | 'downloading'
  | 'paused'
  | 'complete'
  | 'error'
  | 'removed'
  | 'unknown'

export interface TaskFile {
  index: string
  path: string
  length: string
  completedLength: string
  selected: boolean
  uris?: Array<{ uri: string; status: string }>
}

export interface TaskUri {
  uri: string
  status: string
}

// 后端 afd.getPeers 真实字段: peerId/ip/port/bitfield/downloadSpeed/uploadSpeed/seeder.
// AFD wire 协议 还包含 amChoking/peerIsChoking/amInterested/peerIsInterested/
// bitfield hex 解码后的 percentCompleted / clientName (peerId 解码), 但当前 AFD
// 后端未返回这些字段, 全部标记可选, 后端补齐时直接显示。
export interface TaskPeer {
  peerId?: string
  ip: string
  port: number | string
  bitfield?: string
  downloadSpeed: number | string
  uploadSpeed?: number | string
  seeder?: boolean | string
  // Optional extended fields (not yet provided by backend):
  clientName?: string
  isSeeder?: boolean
  isChoking?: boolean
  isInterested?: boolean
  percentCompleted?: number
}

// 后端 afd.getServers 真实字段: uri/currentUri/downloadSpeed. AFD wire 协议 还
// 有 isBt/currentUri/lastSeenAt/seederCount 等, 当前 AFD 后端未返回。
export interface TaskServer {
  uri: string
  currentUri?: string
  downloadSpeed: number | string
  // Optional extended fields (not yet provided by backend):
  isBt?: boolean
  lastSeenAt?: number | string
  seederCount?: number
}

export interface TaskInfo {
  gid: string
  status: TaskState
  totalLength: string
  completedLength: string
  uploadLength: string
  bitfield?: string
  downloadSpeed: string
  uploadSpeed: string
  infoHash?: string
  numSeeders?: string
  seeder?: string
  /** v3.19.0: 原始下载地址 (tellStatus files[0].uris[0].uri 的顶层镜像) */
  url?: string
  pieceLength?: string
  numPieces?: string
  connections?: string
  connectionsMax?: string
  errorCode?: string
  errorMessage?: string
  followedBy?: string[]
  following?: string
  belongsTo?: string
  /** v3.13.58: 镜像任务的执行节点 (其它节点代为执行时非空) */
  targetNode?: string
  /** v3.19.37: 用户自定义分类 (列表筛选) */
  category?: string
  /** v3.19.0: 连接故事 — 协议类型 (http/ftp/sftp/hls/bt) */
  protocol?: string
  /** v3.19.0: 重定向后的最终 URL (已脱敏) */
  resolvedUrl?: string
  /** v3.19.0: 重定向次数 */
  redirectCount?: string
  /** v3.19.0: 生效代理 (已脱敏, 无代理为空) */
  proxy?: string
  dir: string
  files?: TaskFile[]
  bittorrent?: {
    announceList?: string[][]
    comment?: string
    creationDate?: number
    mode?: string
    info?: {
      name?: string
    }
  }
  metalink?: {
    version?: string
  }
  // Derived / convenience fields (computed client-side)
  name?: string
  source?: string
  destination?: string
  progress?: number
  total?: number
  done?: number
  speed?: number
  uploadSpeedNum?: number
  connectionsNum?: number
  /** v3.19.39: 自适应连接数上限 (显示 "4/16 连接") */
  connectionsMaxNum?: number
  eta?: number
  createdAt?: number
  updatedAt?: number
  error?: string
}

export interface AddUriPayload {
  type?: 'uri'
  urls: string[]
  option?: Record<string, unknown>
}

export interface AddTorrentPayload {
  type: 'torrent'
  torrent: string // base64
  option?: Record<string, unknown>
}

export interface AddMetalinkPayload {
  type: 'metalink'
  metalink: string // xml string
  option?: Record<string, unknown>
}

export type AddTaskPayload = AddUriPayload | AddTorrentPayload | AddMetalinkPayload

export interface AddTaskResponse {
  gid: string
}

export interface RetryResponse {
  old_gid: string
  new_gid: string
}

export interface ChangePositionPayload {
  pos: number
  how?: 'SET' | 'CUR' | 'END' | 'POS_SET' | 'POS_CUR' | 'POS_END' | 'OFFSET_SET' | 'OFFSET_CUR' | 'OFFSET_END'
}

// --- Collection endpoints ---

// v3.13.39 fix: 三个列表端点返回 {ok, data} 信封 —— 此前不解包直接把
// 信封对象交给调用方 .map(), TasksView 一进来就崩 (map is not a function)。
export async function listActive(): Promise<TaskInfo[]> {
  const res = await api.get<ApiEnvelope<TaskInfo[]>>('/api/tasks/active')
  return unwrap<TaskInfo[]>(res) ?? []
}

export async function listWaiting(): Promise<TaskInfo[]> {
  const res = await api.get<ApiEnvelope<TaskInfo[]>>('/api/tasks/waiting')
  return unwrap<TaskInfo[]>(res) ?? []
}

export async function listStopped(): Promise<TaskInfo[]> {
  const res = await api.get<ApiEnvelope<TaskInfo[]>>('/api/tasks/stopped', {
    query: { limit: 200 },
  })
  return unwrap<TaskInfo[]>(res) ?? []
}

// --- Single task ---

export async function getTask(gid: string): Promise<TaskInfo> {
  const res = await api.get<ApiEnvelope<TaskInfo>>(`/api/tasks/${encodeURIComponent(gid)}`)
  return unwrap<TaskInfo>(res)
}

export async function getTaskFiles(gid: string): Promise<TaskFile[]> {
  const res = await api.get<ApiEnvelope<TaskFile[]>>(`/api/tasks/${encodeURIComponent(gid)}/files`)
  return unwrap<TaskFile[]>(res) ?? []
}

export async function getTaskUris(gid: string): Promise<TaskUri[]> {
  const res = await api.get<ApiEnvelope<TaskUri[]>>(`/api/tasks/${encodeURIComponent(gid)}/uri`)
  return unwrap<TaskUri[]>(res) ?? []
}

export async function getTaskPeers(gid: string): Promise<TaskPeer[]> {
  const res = await api.get<ApiEnvelope<TaskPeer[]>>(`/api/tasks/${encodeURIComponent(gid)}/peers`)
  return unwrap<TaskPeer[]>(res) ?? []
}

export async function getTaskServers(gid: string): Promise<TaskServer[]> {
  const res = await api.get<ApiEnvelope<TaskServer[]>>(`/api/tasks/${encodeURIComponent(gid)}/servers`)
  return unwrap<TaskServer[]>(res) ?? []
}

// --- v3.17.4 任务详情 v2: 下载内部视角 (1s 轮询) ---

export interface LiveWorker {
  id: number
  blocks: string
  speedBps: number
  doneBytes: number
  active: boolean
}

export interface LiveServer {
  ip: string
  port: string
  speedBps: number
  bytes: number
  conns: number
}

export interface TaskLive {
  mode: 'multi' | 'single' | 'bt' | 'none' | string
  workers: LiveWorker[]
  threadsActive: number
  servers: LiveServer[]
  /** 完成位图 (base64, 每 bit 一块) */
  blocksCompleted?: string
  /** 进行中位图 (base64) */
  blocksInFlight?: string
  blockLength?: number
  numBlocksPerSeg?: number
  blocksOk?: boolean
}

/** v3.18.0 任务日志行 (环形缓冲) */
export interface TaskLogLine {
  ts: string
  level: string
  msg: string
}

/** 204 = 任务不在执行中; 调用方保留最后一次数据 */
export async function getTaskLive(gid: string): Promise<TaskLive | null> {
  try {
    const res = await api.get<TaskLive | null>(`/api/tasks/${encodeURIComponent(gid)}/live`)
    if (res === null || res === undefined) return null
    // 204 空体 → client 可能返回 null/空串
    if (typeof res === 'object' && Object.keys(res).length === 0) return null
    return unwrap<TaskLive>(res)
  } catch {
    return null
  }
}

/** v3.18.0 任务日志尾部 (默认 200 行) */
export async function getTaskLog(gid: string, tail = 200): Promise<TaskLogLine[]> {
  try {
    // v3.18.5 fix: handler 走 okJSON 包装, 未解包导致任务日志卡一直空。
    const res = await api.get<ApiEnvelope<{ lines: TaskLogLine[] }>>(
      `/api/tasks/${encodeURIComponent(gid)}/log?tail=${tail}`,
    )
    return unwrap<{ lines: TaskLogLine[] }>(res)?.lines ?? []
  } catch {
    return []
  }
}

/** v3.18.0 服务端日志页 (runtime / audit) */
export async function getServerLogs(
  kind: 'runtime' | 'audit',
  tail = 300,
  grep = '',
): Promise<{ lines: string[]; file: string; configured: boolean }> {
  try {
    const q = new URLSearchParams({ tail: String(tail) })
    if (grep) q.set('grep', grep)
    // v3.18.5 fix: handler 走 okJSON 包装, 未解包导致日志页一直"无日志行"。
    const res = await api.get<ApiEnvelope<{ lines: string[]; file: string; configured: boolean }>>(
      `/api/logs/${kind}?${q.toString()}`,
    )
    return unwrap<{ lines: string[]; file: string; configured: boolean }>(res)
      ?? { lines: [], file: '', configured: false }
  } catch {
    return { lines: [], file: '', configured: false }
  }
}

export async function setTaskOption(gid: string, option: Record<string, string>): Promise<void> {
  return api.post<void>(`/api/tasks/${encodeURIComponent(gid)}/option`, { option })
}

// --- Mutations ---

export async function pauseTask(gid: string): Promise<void> {
  return api.post<void>(`/api/tasks/${encodeURIComponent(gid)}/pause`)
}

export async function resumeTask(gid: string): Promise<void> {
  return api.post<void>(`/api/tasks/${encodeURIComponent(gid)}/unpause`)
}

// v3.19.31: 后端返回 {gid, removed_on, local} — removed_on 是成功清除的
// 对端节点数 (全节点同步删除), 供 toast 提示"已同步删除 N 个节点"。
export interface RemoveTaskResponse {
  gid: string
  removed_on: number
  local?: boolean
}

export async function removeTask(gid: string, force = false, files = false): Promise<RemoveTaskResponse> {
  // SA2 兼容: 用 POST /api/tasks/{gid}/remove (推荐, 支持 force/files)
  return api.post<RemoveTaskResponse>(`/api/tasks/${encodeURIComponent(gid)}/remove`, { force, files })
}

export async function deleteTaskLegacy(gid: string): Promise<void> {
  // 老 REST DELETE /api/tasks/{gid} 路径
  return api.delete<void>(`/api/tasks/${encodeURIComponent(gid)}`)
}

export async function retryTask(gid: string): Promise<RetryResponse> {
  // v3.18.5 fix: okJSON 包装解包 (成功提示此前显示 undefined)
  return unwrap<RetryResponse>(await api.post(`/api/tasks/${encodeURIComponent(gid)}/retry`))
}

export async function changePosition(gid: string, payload: ChangePositionPayload): Promise<void> {
  return api.post<void>(`/api/tasks/${encodeURIComponent(gid)}/position`, payload)
}

export async function addTask(payload: AddTaskPayload): Promise<AddTaskResponse> {
  // v3.18.5 fix: okJSON 包装解包 (成功提示此前显示 undefined)
  return unwrap<AddTaskResponse>(await api.post('/api/tasks/add', payload))
}

export async function purgeStoppedResults(): Promise<void> {
  return api.post<void>('/api/purge-result')
}

// --- Helpers ---

const NUM = (v: unknown): number => {
  if (typeof v === 'number') return v
  if (typeof v === 'string') {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

/**
 * Normalize a raw TaskInfo (from AFD wire 协议) to convenient numeric
 * fields used across the UI. Mutates and returns the same object for callers
 * that want to keep the original reference.
 */
export function normalizeTask(task: TaskInfo): TaskInfo {
  const total = NUM(task.totalLength)
  const done = NUM(task.completedLength)
  const speed = NUM(task.downloadSpeed)
  const upSpeed = NUM(task.uploadSpeed)
  const conns = NUM(task.connections)

  const bt = task.bittorrent?.info?.name
  const dir = task.dir ?? ''
  // v3.13.53 fix: 任务名优先取 files[0].path 的文件名 (后端合成 files 时
  // path=OutputPath)。旧逻辑 deriveNameFromDir 取的是 dir 的目录名 —
  // 所有 HTTP 任务都显示成下载目录名 (如 "TrAf"), 用户以为"没有文件信息"。
  const filePath = Array.isArray(task.files) && task.files[0]?.path
  const fileBase = filePath ? (filePath.split(/[\\/]/).pop() ?? '') : ''
  task.name =
    bt && bt.length > 0 ? bt : fileBase || deriveNameFromDir(dir) || task.gid
  task.total = total
  task.done = done
  task.progress = total > 0 ? Math.min(100, (done / total) * 100) : 0
  task.speed = speed
  task.uploadSpeedNum = upSpeed
  task.connectionsNum = conns
  task.connectionsMaxNum = NUM(task.connectionsMax) || 0
  task.eta = speed > 0 ? Math.max(0, (total - done) / speed) : -1
  task.source = (Array.isArray(task.files) && task.files[0]?.uris?.[0]?.uri) || undefined
  task.destination = dir
  if (task.errorCode && task.errorCode !== '0') {
    task.error = task.errorMessage || `RPC error ${task.errorCode}`
  }
  return task
}

function deriveNameFromDir(dir: string): string {
  if (!dir) return ''
  const cleaned = dir.replace(/[\\/]+$/, '')
  const idx = cleaned.lastIndexOf('/')
  const idx2 = cleaned.lastIndexOf('\\')
  const cut = Math.max(idx, idx2)
  return cut >= 0 ? cleaned.slice(cut + 1) : cleaned
}

/**
 * Map wire status code → UI label key.
 */
export function statusLabelKey(state: TaskState): string {
  switch (state) {
    case 'active':
    case 'downloading':
      return 'tasks.state.downloading'
    case 'paused':
      return 'tasks.state.paused'
    case 'waiting':
    case 'pending':
      return 'tasks.state.waiting'
    case 'complete':
      return 'tasks.state.complete'
    case 'error':
      return 'tasks.state.error'
    case 'removed':
      return 'tasks.state.removed'
    default:
      return 'tasks.state.unknown'
  }
}
// v3.19.37: 任务清单导出/导入 (换机备份)。
export async function exportTasks(): Promise<void> {
  const res = await fetch('/api/tasks/export', { credentials: 'include' })
  if (!res.ok) throw new Error(`export failed: HTTP ${res.status}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'afd-tasks-export.json'
  a.click()
  URL.revokeObjectURL(url)
}

export interface ImportResult {
  ok: boolean
  added: number
  skipped: number
  failed: number
  total: number
}

export async function importTasks(file: File): Promise<ImportResult> {
  const text = await file.text()
  const res = await fetch('/api/tasks/import', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: text,
  })
  if (!res.ok) throw new Error(`import failed: HTTP ${res.status}`)
  return res.json()
}
